import { DateUtils, ScheduleDoc, TagData, TaskDoc } from 'common';
import { ConcourseDb } from '../../../global/ConcourseDb';
import { Task, TaskType } from './Task';

export interface SearchParams {
  name?: string;
  searchTerm: string;
  orConditions: any[];
  startDate: string | null;
  finishDate: string | null;
  dateFilterType: string;
  lookaheadStartType: string;
  lookaheadWeeks: number | null;
  criticalPath: boolean;
}

export interface FilterDoc extends SearchParams {
  _id?: string;
  _rev?: string;
}

export class ScheduleFilter {
  static getFilterDb(projectId: string): ConcourseDb {
    return ConcourseDb.getInstance(`p-${projectId}-schedule-filters`);
  }

  static async saveFilter(projectId: string, filter: FilterDoc): Promise<FilterDoc[]> {
    const db = await ScheduleFilter.getFilterDb(projectId);
    await db.saveDoc(filter);
    return await db.getDocs();
  }

  static async deleteFilter(projectId: string, filter: FilterDoc): Promise<FilterDoc[]> {
    const db = await ScheduleFilter.getFilterDb(projectId);
    await db.remove(filter);
    return await db.getDocs();
  }

  static async getFilters(projectId: string): Promise<FilterDoc[]> {
    const db = await ScheduleFilter.getFilterDb(projectId);
    return await db.getDocs();
  }

  static async getSelectedFilters(projectId: string, docIds: string[]): Promise<FilterDoc[]> {
    const docs = await ScheduleFilter.getFilters(projectId);
    return docs.filter(d => docIds.includes(d._id));
  }

  static async getFilter(projectId: string, docId: string): Promise<FilterDoc> {
    const docs = await ScheduleFilter.getFilters(projectId);
    return docs.find(d => d._id == docId);
  }


  /**
   * Apply multiple filters sequentially to tasks
   * @param tasks - Array of tasks to filter
   * @param filters - Array of filter documents
   * @returns Filtered tasks including their parents
   
  static getAllNodes(treeNode: TreeNode): TreeNode[] {
    const allNodes: TreeNode[] = [];
    const findAllNodesFromParent = (treeNode: TreeNode): TreeNode[] => {
      if (treeNode.isParent) {
        allNodes.push(...treeNode.children);
        treeNode.children.forEach(child => {
          findAllNodesFromParent(child);
        });
      }
      return allNodes;
    };
    return findAllNodesFromParent(treeNode);
  }
    */

  static filterTasks(tasks: TaskDoc[], params: FilterDoc): TaskDoc[] {
    // this is actually doing an AND search.
    let filteredNodes: TaskDoc[] = [...tasks.filter(task => task.type != TaskType.WORKFLOW_STATE)];
    for (const condition of params.orConditions) {
      if (condition.searchTerm) {
        filteredNodes = filteredNodes.filter(node => this.includesTerm(node, condition.searchTerm));
      } else {
        filteredNodes = filteredNodes.filter(node => this.hasTag(node, condition));
      }
    }

    // Then filter the results using AND (each term reduces the results)
    if (params.searchTerm && params.searchTerm.length > 2) {
      for (let term of params.searchTerm.split(' ')) {
        filteredNodes = filteredNodes.filter(node => this.includesTerm(node, term));
      }
    }
    if (params.criticalPath) {
      filteredNodes = filteredNodes.filter(node => this.isOnCriticalPath(node));
    }

    if (params.dateFilterType != 'none' && params.startDate && params.finishDate) {
      const startDate = DateUtils.fromISO(params.startDate);
      const finishDate = DateUtils.fromISO(params.finishDate);
      filteredNodes = filteredNodes.filter(node => this.taskHappensDuringDates(node, startDate, finishDate));
    }

    return filteredNodes;
  }


  static includesTerm(node: TaskDoc, term: string): boolean {
    return node.name.toLowerCase().includes(term.toLowerCase()) || node.name.localeCompare(term, undefined, { sensitivity: 'accent' }) === 0;
  }

  static hasTag(task: TaskDoc, tag: TagData): boolean {
    return (task.tags.findIndex(t => t.name == tag.name) > -1);
  }

  static isOnCriticalPath(task: TaskDoc): boolean {
    return task.total_slack == null || task.total_slack == 0;
  }

  static taskHappensDuringDates(task: TaskDoc, start: Date, finish: Date): boolean {
    const startStr = task.actual_start || task.start || task.planned_start;
    const startDate = start ? DateUtils.fromISO(startStr) : null;
    const finishStr = task.actual_finish || task.finish || task.planned_finish;
    const finishDate = finish ? DateUtils.fromISO(finishStr) : null;

    return (startDate <= finish && finishDate >= start);
  }

  static getStartEndFromTasks(tasks: Task[], schedule: ScheduleDoc): { startDate: Date; endDate: Date } {
    const projectStartDate = DateUtils.parseISO(schedule?.property_values?.start_date);
    const projectEndDate = DateUtils.parseISO(schedule?.property_values?.finish_date);
    if (tasks.length == 0) {
      return { startDate: projectStartDate, endDate: projectEndDate };
    }
    const startDate = new Date(Math.min(...tasks.map(task => task.startDate.getTime()), projectStartDate.getTime()));
    const endDate = new Date(Math.max(...tasks.map(task => task.finishDate.getTime()), projectEndDate.getTime()));

    return { startDate: startDate, endDate: endDate };
  }
    
}
